// ─── PER-PLAYER PREFERENCE / ENGAGEMENT PROFILES ────────────────────────────
//
// An AI-generated profile of what each real player (Haley / Nicole) enjoys and
// what drives their engagement — built from a broad sample of THEIR OWN writing
// (in-character + out-of-character in rp_history) plus light engagement signals.
// Injected into character prompts so the AI naturally plays TO each person's
// tastes (never mentioned in-character). Refreshed periodically, cached in the
// player_preferences table so it's one read per room, not per reply.
//
// Sibling to getPlayerVoiceSamples (how they WRITE) — this is what they LOVE.

import { supabase, safeGetSession } from './supabaseClient.js';
import { AI_ABORT_MS } from './aiTimeouts.js';

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const REFRESH_AFTER_DAYS = 7;      // rebuild a profile at most weekly
// Writers whose profile write failed this session. Staleness is measured on
// updated_at, so a rejected write can never clear it — retrying would pay for
// the same ~50k-token analysis on every call. Session-scoped on purpose: a
// reload retries once, which is the right cadence for a transient failure.
const failedWriters = new Set();
// A failed analysis (timeout, non-OK, empty, rejected write) backs the writer
// off for half a day ACROSS reloads, not just this session. Before 2026-10-05
// only a rejected write marked the writer failed; a timeout was caught, logged
// and forgotten, so every DM turn re-paid the same ~35k-token analysis —
// Nicole's refresh failed eight times running (seven timeouts in one day).
const REFRESH_BACKOFF_MS = 12 * 60 * 60 * 1000;
const backoffKey = (w) => `mfml_pref_refresh_failed_${w}`;
function markRefreshFailed(w, why) {
  failedWriters.add(w);
  try { localStorage.setItem(backoffKey(w), String(Date.now())); } catch { /* memory only */ }
  console.warn(`[playerPreferences] ${w}: profile refresh failed (${why}) — backing off ${REFRESH_BACKOFF_MS / 3600000}h`);
}
function refreshBackedOff(w) {
  if (failedWriters.has(w)) return true;
  try {
    const at = Number(localStorage.getItem(backoffKey(w)) || 0);
    return at > 0 && (Date.now() - at) < REFRESH_BACKOFF_MS;
  } catch { return false; }
}
// Raised from 140. The profile is meant to describe a PERSON, and 140 lines
// drawn only from the newest ~1,120 rows described their last few weeks. The
// archive holds 155k+ rows spanning ten years; cost is not the constraint here
// (DeepSeek), fidelity is.
const SAMPLE_TARGET = 420;         // how many of their lines to analyze
// Channels where they speak AS THEMSELVES. Getting this list wrong costs
// twice: an OOC channel that is missing is not merely absent from the
// over-weighted third, it is fed to the analyser labelled [rp], as though it
// were their in-character prose.
//
// Verified 2026-08-03 by reading real lines from every candidate channel, not
// by matching on the name. The big miss was `the-restaurant-chat-channel` —
// 1,012 rows of "Oof I hate bulletin boards" and "I forgot it was Juneteenth
// lol", the single largest body of them talking as themselves outside `ooc`.
//
// DELIBERATELY NOT INCLUDED, having read them: `rfa-chat` is in-character
// Mystic Messenger roleplay ("_Evelyn has left the chatroom_"), and
// `special-summer-cruz-event` / `limited-special-cruz-birthday-event` are
// played RP events despite the chatty names.
const OOC_CHANNELS = [
  'ooc', 'ooc-gen', 'general', 'mm_chatroom', 'wifey-chat', 'secret-wifey-chat',
  'chat', 'spam',
  // added 2026-08-03, +1,172 rows of verified out-of-character writing
  'the-restaurant-chat-channel', 'spam-pics', 'memes_and_other_trash_spam',
  'requests_and_ideas', 'future-route-ideas', 'vn-ideas',
];

// ─── REAL-LIFE PRIVATE DATA — never sampled into a prompt ───────────────────
//
// Haley, 2026-08-05: "you can take out the financial info and weird personal
// info from eric's profile but keep the characterizations."
//
// The OOC channels are where she and Nicole talk as themselves, which is
// exactly why they are the best voice evidence — and also why they carry
// real-life admin about real people. Auditing the `eric` record found that
// 192 of its 197 archived passages are OOC messages about her actual partner,
// aggregating his employer and boss, a resignation and a job offer, a therapy
// appointment, a doctor's appointment, household finances and home equity, a
// gym schedule, and a pasted text exchange with two full legal names. Five of
// the seven channels holding it are in OOC_CHANNELS, so those rows were
// eligible to be sampled VERBATIM into prompts by getWriterVoiceSection, and
// to be fed to the analyser by refreshPlayerPreferences.
//
// This drops rows that read as real-life private admin, in BOTH paths. It is
// deliberately about SUBJECT MATTER, not about any one person: nothing here
// names Eric, so it protects Nicole's and Haley's own medical, financial and
// employment details identically.
//
// SCOPED TIGHT ON PURPOSE — and the first version was too broad, which is
// worth recording because the failure was subtle. Blanket-matching the words
// `therapy|therapist|psychiatr` removed 2% of real OOC rows, and reading them
// showed what that cost: "I made the mistake of telling my mom I might talk to
// a therapist about what happened growing up", "the first thing he said was
// that now three of my kids don't love me", "it'll prob get a little easier
// after trauma therapy". That is HER OWN emotional history — the load-bearing
// material her deep_profile is built from and that she deliberately fed into
// the system. Her instruction scoped this to a third party's records, not her
// inner life, and "keep the characterizations" was half of it.
//
// So these patterns match RECORDS, not TOPICS. The test is whether a line
// carries a fact someone could act on — a figure, an identifier, a scheduled
// appointment, an employment event — rather than a feeling about a subject.
//   removed: "I have therapy at 4"        (a schedule)
//   kept:    "I might talk to a therapist about what happened growing up"
//   removed: "he finally put in his resignation, last day is the 14th"
//   kept:    "I am exhausted and I hate my job right now"
const PRIVATE_LIFE_PATTERNS = [
  // Money as a figure, and the instruments that carry figures.
  /\$\s?\d|\b\d+\s?k\b(?!\s*(?:words|tokens|tok))|\bequity\b|\bmortgage\b|\brefinanc/i,
  /\bsalar(?:y|ies)\b|\b401k\b|\bseverance\b|\bnet worth\b|\btaxes?\b(?=[^.!?]{0,25}\b(?:filed|owe|refund|return))/i,
  // Employment as an EVENT with a consequence, not as a mood.
  /\bgot the (?:job|offer|position)\b|\bthe job offer\b|\bresignation\b|\bresigned\b|\bput in (?:his|her|my|their) notice\b|\blaid off\b|\bstart date\b/i,
  // Medical/appointment ADMIN — a time, a prescription, an insurer, a result.
  // Deliberately NOT the bare words therapy/therapist/diagnosis/adhd/ocd.
  /\b(?:therapy|therapist|doctor'?s?|dentist|appointment|surgery)\b[^.!?]{0,28}\b(?:at|on)\s+\d/i,
  /\bappointment (?:is|was) (?:at|on)\b|\bprescription\b|\bpharmacy\b|\bcopay\b|\binsurance\b(?=[^.!?]{0,30}\b(?:cover|plan|claim|deductible))/i,
  /\bcovid test|\bblood work\b|\btest (?:came back|results)\b/i,
  // Hard identifiers.
  /\baccount number\b|\brouting number\b|\bssn\b|\bsocial security\b|\bpolicy number\b|\bpassport number\b/i,
];

/** True when a row reads as real-life private admin rather than voice. */
export function isPrivateLifeRow(text) {
  const s = String(text || '');
  return PRIVATE_LIFE_PATTERNS.some(re => re.test(s));
}

const ANALYSIS_PROMPT = `You are building a private working profile of ONE REAL PERSON who writes on a small adult collaborative-fiction platform with one friend. AI characters read this before replying to her, so that scenes land for HER — the writer — not merely for the character she happens to be playing.

That distinction is the entire point. She plays characters, but she writes for herself. Describe the PERSON.

You are reading a broad sample spanning years of her writing. Lines marked [OOC] are her speaking as herself, out of character — weight those most heavily, they are the most direct evidence of who she is.

Write these sections, tightly:

1. WHAT SHE IS ACTUALLY HERE FOR — the emotional experience she keeps returning for, underneath any particular ship or plot. What is the feeling she is chasing?
2. WHAT MAKES HER FEEL SEEN — the specific moves, lines, and gestures that visibly land: what she responds to at length, what she quotes back, what she softens at. Be concrete.
3. WHAT SHE ASKS FOR VS WHAT SHE RESPONDS TO — people often request one thing and light up at another. Name the gap if the evidence shows one.
4. HER PATTERNS AS A WRITER — how she opens, how she escalates, what she does when a scene stalls, how she signals she is invested versus going through the motions.
5. WHAT DEFLATES HER — what makes her disengage, go short, change the subject, or drop a thread.
6. HOW TO GIVE HER A GOOD SCENE — practical direction for a character talking to her: what to notice, what to do, what to never do.

Rules:
- Ground every claim in the sample. If the evidence is thin for a section, say so plainly rather than inventing.
- This is about what she needs from a STORY, not a clinical assessment of a person. No diagnoses, no pathologising, no armchair therapy. Warm, specific, respectful — the way a good writing partner who has known her for years would describe her.
- Never suggest a character reveal, quote, or reference this profile. It shapes behaviour only.

Output ONLY the profile. Under 500 words.`;

// Login identity → writer key. Display names can be edited, differ per
// channel, or be replaced by a character name in RP — which is exactly how the
// preference profile silently stopped loading (the RP call site was passing
// "Kya"). Auth user ids cannot drift, so they are the authoritative mapping and
// the display name is only a fallback.
const WRITER_BY_USER_ID = {
  '6db164e7-0419-470c-a8fe-2ddb899f5578': 'haley',
  '8420ef71-0898-41e0-ac6c-6916969b82f2': 'nicole',
};

// Personal handles (lowercased) from writer_accounts. ONLY the handles they
// use as THEMSELVES — the RP-persona aliases in that table (Cruz, Lance,
// Kaleb, Kya…) are deliberately excluded because they collide with live AI
// character names, and mapping "Lance" → haley would misattribute a
// character's identity to a person.
const WRITER_BY_HANDLE = {
  haley: 'haley',
  princesscomplex007: 'haley',
  nicole: 'nicole',
  steelergirl101101: 'nicole',
  steelergirl7304: 'nicole',
};

/**
 * Map identifiers → writer keys we have data for.
 * Accepts auth user ids (authoritative), display names, and personal handles
 * (fallbacks), so callers can pass whichever they hold.
 */
function toWriters(identifiers) {
  const out = new Set();
  for (const raw of (identifiers || [])) {
    const v = String(raw || '').trim();
    if (!v) continue;
    const byId = WRITER_BY_USER_ID[v];
    if (byId) { out.add(byId); continue; }
    const byHandle = WRITER_BY_HANDLE[v.toLowerCase()];
    if (byHandle) out.add(byHandle);
  }
  return [...out];
}

/**
 * Read stored preference profiles for the given players.
 * @returns {Promise<Object>} { haley: {profile, updated_at, based_on_count}, ... }
 */
export async function getPlayerPreferenceProfiles(displayNames) {
  const writers = toWriters(displayNames);
  if (!writers.length) return {};
  try {
    const { data } = await supabase
      .from('player_preferences')
      .select('writer, profile, deep_profile, stated_notes, updated_at, based_on_count')
      .in('writer', writers);
    const out = {};
    for (const row of (data || [])) {
      // Any layer counts — a writer with only a deep_profile or stated_notes
      // (no weekly profile yet) must still render.
      if (row.profile || row.deep_profile || row.stated_notes) out[row.writer] = row;
    }
    return out;
  } catch (err) {
    console.warn('[playerPreferences] read failed:', err?.message);
    return {};
  }
}

/**
 * Format profiles into a prompt section. Keyed by the display names in the room
 * so the AI knows which real person each profile belongs to. Empty when none.
 */
export function formatPreferencesForPrompt(profiles, displayNames) {
  const writers = toWriters(displayNames);
  // Preserve the original display-name casing for the header.
  const labelFor = (w) => (displayNames || []).find(n => String(n || '').toLowerCase().trim() === w) || (w.charAt(0).toUpperCase() + w.slice(1));
  const blocks = writers
    .filter(w => profiles[w]?.profile || profiles[w]?.stated_notes || profiles[w]?.deep_profile)
    .map(w => {
      // THREE LAYERS, IN RANK ORDER. Haley, 2026-08-03: "put both as main
      // psychological pulls for the rp in addition to the one that refreshes."
      //
      //   1. stated_notes — what she TOLD US directly. Authoritative; the
      //      auto-refresh never touches it. Includes corrections (e.g. she
      //      never called the police on her father — she knew it would be
      //      too dangerous); a correction here overrides the documents below.
      //   2. deep_profile — the MAIN pull: the complete profile built from a
      //      full read of her decade of OOC writing. Never auto-refreshed.
      //   3. profile — the weekly self-refreshing layer, now additive; it
      //      carries what is NEW since the deep read.
      //
      // These are large (~16k chars per writer) and byte-stable between
      // turns, which is why they belong in the CACHED half of the prompt —
      // the caller is responsible for placing this block before the cache
      // breakpoint.
      const stated = profiles[w]?.stated_notes
        ? `WHAT SHE HAS TOLD US DIRECTLY — authoritative, outranks everything below (including any contradicting line in the documents that follow):\n${profiles[w].stated_notes.trim()}\n\n`
        : '';
      const deep = profiles[w]?.deep_profile
        ? `HER COMPLETE PROFILE — the main psychological reference, built from a full read of everything she has written as herself for ten years:\n${profiles[w].deep_profile.trim()}\n\n`
        : '';
      const inferred = profiles[w]?.profile
        ? `RECENT ADDITIONS — refreshed weekly from her latest writing; extends the profile above:\n${profiles[w].profile.trim()}`
        : '';
      return `── ${labelFor(w)} — the real person you are writing for (play to this; NEVER mention or reference it in-character) ──\n${stated}${deep}${inferred}`;
    });
  if (!blocks.length) return '';
  return `\n\nPLAYING TO YOUR WRITERS — these are the real people you're writing with. Each has personal tastes in what makes a scene land for them. Lean naturally into what lights them up, WITHOUT ever breaking character or hinting you know this:\n\n${blocks.join('\n\n')}`;
}

/**
 * One-call convenience for AI surfaces: fetch + format the profile section
 * for whoever the identifiers resolve to, and kick the weekly refresh.
 * Pass auth user ids first (authoritative), display names after (labels the
 * header). Returns '' when nobody resolves — safe to interpolate directly.
 * The block is byte-stable between turns; place it in the CACHED prompt half.
 */
export async function getWriterProfileSection(identifiers) {
  const ids = (identifiers || []).filter(Boolean);
  if (!ids.length) return '';
  try {
    const profs = await getPlayerPreferenceProfiles(ids);
    maybeRefreshPreferences(ids);
    return formatPreferencesForPrompt(profs, ids);
  } catch { return ''; }
}

// ─── WRITER VOICE — "my drafts should sound like MY writing" ────────────────
//
// Haley, 2026-08-03: "my draft calls should sound like my writing and
// nicole's draft calls should sound like hers." The ghost-writing surfaces
// were matching the SCENE's voice; this section teaches them the WRITER's
// voice, from her own decade of writing in rp_history.
//
// Two registers, because each player writes as two different people:
//   'prose'  — their in-scene RP writing (stories, channels): narration
//              style, beat length, asterisk/quote habits.
//   'casual' — their own typing voice from the OOC channels: how they text
//              as THEMSELVES (used for DM drafts, where the human writes as
//              herself, not a character).
//
// Recency-weighted on purpose (NOT year-balanced like the psych sampler):
// a draft should sound like 2026-her, not 2020-her.

/** Pure formatter — exported for tests. rows: [{content, channel_name}] */
export function formatWriterVoiceSection(name, rows, register = 'prose') {
  const samples = (rows || []).map(r => String(r?.content || '').trim()).filter(Boolean);
  if (!samples.length) return '';
  const what = register === 'casual'
    ? 'texting/chatting as HERSELF'
    : 'writing in-scene RP prose';
  return `\n\n═══ HOW ${String(name || 'THE WRITER').toUpperCase()} ACTUALLY WRITES — HER OWN WORDS ═══
These are VERBATIM samples of ${name} ${what}. A draft for her must read like SHE wrote it — her sentence rhythm, her punctuation and capitalization habits, her formatting (asterisks, quotes, dashes, paragraph breaks), her typical message length, her word choices. Imitate the VOICE, never the content: do not reuse these events, names or lines.
${samples.map((s, i) => `--- sample ${i + 1} ---\n${s}`).join('\n')}
═══════════════════════════════════════════`;
}

const WRITER_VOICE_CACHE = new Map(); // `${writer}|${register}` → { at, rows }
// 6 hours, persisted (2026-09-03). The corpus is a static decade of writing,
// so re-reading 87K rows to pick the newest 500 on every reload bought
// nothing — and on the phone, every backgrounded PWA is a reload. Measured:
// the read is 2.4-4.4s in production per draft; a memory-only 30-minute cache
// rarely survived to the second draft. localStorage carries it across
// reloads; a failed read (private mode, quota) just falls back to memory.
const WRITER_VOICE_TTL_MS = 6 * 60 * 60 * 1000;
const VOICE_LS_PREFIX = 'mfml_voice_rows:';
function readVoiceCache(key) {
  const mem = WRITER_VOICE_CACHE.get(key);
  if (mem) return mem;
  try {
    const raw = localStorage.getItem(VOICE_LS_PREFIX + key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || !Array.isArray(parsed.rows) || typeof parsed.at !== 'number') return null;
    WRITER_VOICE_CACHE.set(key, parsed);
    return parsed;
  } catch { return null; }
}
function writeVoiceCache(key, value) {
  WRITER_VOICE_CACHE.set(key, value);
  try { localStorage.setItem(VOICE_LS_PREFIX + key, JSON.stringify(value)); } catch { /* memory only */ }
}

/**
 * Fetch the picked sample rows for one writer+register. Shared by the draft
 * voice (sound like ME) and the counterpart voice (characters sound like the
 * OTHER writer). One recent-first corpus read per writer+register per 30 min.
 *
 * The registers query DIFFERENTLY on purpose: verified against the live
 * corpus, the most recent 500 rows contain ZERO OOC-channel rows (the OOC
 * writing is mostly 2020-era), so deriving 'casual' from a shared recent
 * window silently rendered nothing. 'casual' therefore queries the OOC
 * channels directly — recent-first WITHIN them.
 */
async function fetchWriterVoiceRows(writer, register, maxSamples, maxChars) {
  const cacheKey = `${writer}|${register}`;
  let cached = readVoiceCache(cacheKey);
  if (!cached || Date.now() - cached.at >= WRITER_VOICE_TTL_MS) {
    const { data: accts } = await supabase
      .from('writer_accounts').select('account_name').eq('writer', writer);
    const accounts = (accts || []).map(a => a.account_name);
    if (!accounts.length) return [];
    // One bounded backward walk PER ACCOUNT (writer_recent_rows, 2026-09-03)
    // instead of `author IN (19 accounts) ORDER BY time DESC LIMIT 500`: that
    // shape made Postgres read all 87K of Haley's rows and sort them on every
    // cold call (2.4-4.4s in production), because no index gives a global
    // time order across authors. The RPC walks each account's newest rows via
    // the (author, message_timestamp) index and stops early. p_min_len skips
    // rows the picker below would drop anyway. The old query stays as the
    // fallback so a missing RPC (a stale deploy) still yields a voice.
    const wantOOCRows = register === 'casual';
    let data = null;
    try {
      const { data: rpcRows, error: rpcErr } = await supabase.rpc('writer_recent_rows', {
        p_accounts: accounts,
        p_per_account: wantOOCRows ? 80 : 150,
        p_channels: wantOOCRows ? OOC_CHANNELS : null,
        p_min_len: wantOOCRows ? 40 : 120,
      });
      if (rpcErr) throw rpcErr;
      data = rpcRows;
    } catch (e) {
      console.warn('[playerPreferences] writer_recent_rows unavailable, falling back:', e?.message || e);
      let q = supabase
        .from('rp_history')
        .select('content, channel_name')
        .in('author', accounts)
        .order('message_timestamp', { ascending: false });
      q = wantOOCRows ? q.in('channel_name', OOC_CHANNELS).limit(300) : q.limit(500);
      ({ data } = await q);
    }
    cached = { at: Date.now(), rows: data || [] };
    writeVoiceCache(cacheKey, cached);
  }
  const ooc = new Set(OOC_CHANNELS);
  const wantOOC = register === 'casual';
  const minLen = wantOOC ? 40 : 120;
  const seen = new Set();
  const picked = [];
  let used = 0;
  for (const r of cached.rows) {
    // The prose query is unfiltered at the DB — drop OOC rows here. (The
    // casual query is already OOC-only; the check is a no-op there.)
    if (ooc.has(r.channel_name) !== wantOOC) continue;
    const content = String(r.content || '').trim();
    // Real-life private admin never becomes a verbatim voice sample. This is
    // the path that put raw text straight into a prompt, so it is the one that
    // mattered most (see PRIVATE_LIFE_PATTERNS).
    if (isPrivateLifeRow(content)) continue;
    // rp_history carries exact-duplicate rows (26k of them) — dedupe here.
    if (content.length < minLen || seen.has(content)) continue;
    if (used + content.length > maxChars && picked.length) break;
    seen.add(content);
    picked.push(r);
    used += content.length;
    if (picked.length >= maxSamples) break;
  }
  return picked;
}

const labelOf = (w) => w.charAt(0).toUpperCase() + w.slice(1);

export async function getWriterVoiceSection(identifiers, { register = 'prose', maxSamples = 12, maxChars = 7000 } = {}) {
  const writers = toWriters(identifiers);
  if (!writers.length) return '';
  const writer = writers[0];
  try {
    const picked = await fetchWriterVoiceRows(writer, register, maxSamples, maxChars);
    return formatWriterVoiceSection(labelOf(writer), picked, register);
  } catch { return ''; }
}

// ─── COUNTERPART VOICE — the hand that has always written your characters ──
//
// Haley, 2026-08-03: "make sure that the ai characters sound like me if
// they're writing to nicole and sound like her if they're writing to me
// with typing choices, word choices, grammar etc."
//
// In a decade of two-writer play, every character who speaks TO Haley was
// written BY Nicole, and every character who speaks TO Nicole was written
// BY Haley. An AI character continuing that work should hold the same pen:
// the OTHER writer's prose habits, not a generic AI register.

const COUNTERPART = { haley: 'nicole', nicole: 'haley' };

/** Pure formatter — exported for tests. */
export function formatCounterpartVoiceSection(counterpartName, currentName, rows) {
  const samples = (rows || []).map(r => String(r?.content || '').trim()).filter(Boolean);
  if (!samples.length) return '';
  return `\n\n═══ THE HAND THAT WRITES YOU — ${String(counterpartName).toUpperCase()}'S PROSE ═══
For ten years, every character who speaks to ${currentName} in this story has been written by ${counterpartName} — a real person with a distinct writing voice. Your replies continue HER work, in HER hand: her typing choices, word choices, grammar, punctuation and capitalization habits, her formatting (asterisks, quotes, dashes, paragraph shapes), her sentence rhythm and typical beat length. These are VERBATIM samples of ${counterpartName}'s own writing. Imitate the VOICE, never the content — do not reuse these events, names or lines, and stay YOUR character while writing in her hand.
${samples.map((s, i) => `--- sample ${i + 1} ---\n${s}`).join('\n')}
═══════════════════════════════════════════`;
}

/**
 * The counterpart-voice block for an AI character replying to whoever the
 * identifiers resolve to. Byte-stable between turns (30-min cached rows) —
 * safe for the CACHED half of a prompt.
 */
export async function getCounterpartVoiceSection(identifiers, { register = 'prose', maxSamples = 10, maxChars = 6000 } = {}) {
  const writers = toWriters(identifiers);
  if (!writers.length) return '';
  const current = writers[0];
  const other = COUNTERPART[current];
  if (!other) return '';
  try {
    const picked = await fetchWriterVoiceRows(other, register, maxSamples, maxChars);
    return formatCounterpartVoiceSection(labelOf(other), labelOf(current), picked);
  } catch { return ''; }
}

/** Light engagement signals from the writer's corpus (cheap, non-AI). */
async function computeSignals(accounts) {
  try {
    const { data } = await supabase
      .from('rp_history')
      .select('content, channel_name')
      .in('author', accounts)
      .order('message_timestamp', { ascending: false })
      .limit(600);
    const rows = (data || []).filter(r => r.content);
    if (!rows.length) return { count: 0 };
    const lengths = rows.map(r => String(r.content).length);
    const avgLen = Math.round(lengths.reduce((a, b) => a + b, 0) / lengths.length);
    const longShare = Math.round(100 * lengths.filter(l => l > 400).length / lengths.length);
    // Which channels they're most active in (rough "what they engage with").
    const chanCount = {};
    for (const r of rows) { const c = r.channel_name || '?'; chanCount[c] = (chanCount[c] || 0) + 1; }
    const topChannels = Object.entries(chanCount).sort((a, b) => b[1] - a[1]).slice(0, 6).map(([c]) => c);
    return { count: rows.length, avgLen, longReplyPct: longShare, topChannels };
  } catch { return { count: 0 }; }
}

/**
 * Rebuild ONE writer's preference profile: sample their writing, compute
 * signals, run the analysis, store it. Fire-and-forget.
 */
export async function refreshPlayerPreferences(writer) {
  const w = String(writer || '').toLowerCase().trim();
  if (w !== 'haley' && w !== 'nicole') return;
  try {
    const { data: accts } = await supabase
      .from('writer_accounts').select('account_name').eq('writer', w);
    const accounts = (accts || []).map(a => a.account_name);
    if (!accounts.length) return;

    // Broad, spread sample across their corpus (in-character AND ooc) so the
    // analysis sees range, not just the last rapid exchange.
    // PERF-1: the old newest-first .limit(SAMPLE_TARGET*24) was silently
    // capped at 1,000 rows by hosted PostgREST — "spanning years" was really
    // the last ~12 weeks. sample_rp_history strides evenly across the
    // writer's ENTIRE corpus server-side (probe-verified: 1,000 rows
    // spanning 2016→2026) and stays under the response cap by design.
    // The prior profile rides along so the refresh is ADDITIVE. Haley,
    // 2026-08-03: "no need to change it unless it just adds some new info to
    // it" — a from-scratch rebuild could lose insights she has already
    // approved (including hand-deepened analysis), so the model is told to
    // preserve and extend, never to start over.
    const { data: priorRow } = await supabase
      .from('player_preferences').select('profile').eq('writer', w).maybeSingle();
    const priorProfile = priorRow?.profile || '';

    const { data: rows, error: sampleErr } = await supabase
      .rpc('sample_rp_history', { p_accounts: accounts, p_limit: 1000 });
    if (sampleErr) { console.warn('[playerPreferences] sample RPC failed:', sampleErr.message); return; }

    // A SECOND, DEDICATED PASS OVER THE OUT-OF-CHARACTER WRITING.
    //
    // The stride above walks the whole corpus, so OOC arrives only in
    // proportion to its share of everything — ~336 of 1,000 rows, of which the
    // analysis keeps ~147. OOC is where they speak as THEMSELVES, and this
    // profile is supposed to describe a person, so it should not be a
    // by-product of a roleplay sample.
    //
    // And a uniform OOC stride would not have helped: measured 2026-08-03,
    // 96% of Haley's 28,927 OOC rows are from 2020 alone (98% of Nicole's
    // 20,061) — those were the years of dedicated #ooc channels. So
    // sample_rp_history_channels balances BY YEAR, giving 2016 and 2024-2026
    // real representation instead of a rounding error.
    let oocSample = [];
    try {
      const { data: od } = await supabase.rpc('sample_rp_history_channels', {
        p_accounts: accounts, p_channels: OOC_CHANNELS, p_limit: 500,
      });
      oocSample = od || [];
    } catch { /* additive — the general stride still carries OOC */ }
    const seenText = new Set();
    const cleaned = [...(rows || []), ...oocSample]
      .filter(r => {
        const k = `${r.message_timestamp}|${String(r.content || '').slice(0, 80)}`;
        if (seenText.has(k)) return false;
        seenText.add(k);
        return true;
      })
      .map(r => ({
        text: String(r.content || '').replace(/\s+/g, ' ').trim(),
        ooc: OOC_CHANNELS.includes(r.channel_name),
        ts: r.message_timestamp,
      }))
      // isPrivateLifeRow: the analyser is over-weighted toward OOC by design
      // (~35% of the sample), so real-life admin about her, Nicole, or anyone
      // they mention would be disproportionately likely to reach the stored
      // profile — which lands in the CACHED half of every character prompt.
      .filter(r => r.text.length >= 20 && !/^https?:\/\//i.test(r.text)
                   && !/^[<:@]/.test(r.text) && !isPrivateLifeRow(r.text));
    if (cleaned.length < 15) return; // not enough to say anything meaningful

    // OOC is where they speak AS THEMSELVES rather than as a character, so it
    // is the most direct evidence of the person — deliberately over-weighted
    // to about a third of the sample even though it is a minority of rows.
    const oocRows = cleaned.filter(r => r.ooc);
    const icRows = cleaned.filter(r => !r.ooc);
    const spread = (arr, n) => {
      if (arr.length <= n) return arr.slice();
      const step = arr.length / n;
      const out = [];
      for (let i = 0; out.length < n && Math.floor(i) < arr.length; i += step) out.push(arr[Math.floor(i)]);
      return out;
    };
    const oocWanted = Math.min(oocRows.length, Math.round(SAMPLE_TARGET * 0.35));
    const sample = [...spread(oocRows, oocWanted), ...spread(icRows, SAMPLE_TARGET - oocWanted)]
      .sort((a, b) => new Date(a.ts || 0) - new Date(b.ts || 0));

    const signals = await computeSignals(accounts);
    const sampleText = sample.map(s => `${(s.ts ? String(s.ts).slice(0, 7) : '?')} ${s.ooc ? '[OOC]' : '[rp]'} ${s.text.slice(0, 500)}`).join('\n');
    const priorBlock = priorProfile
      ? `\n\nPRIOR PROFILE — treat as established. PRESERVE its insights and its voice; EXTEND it where the sample below adds something new; correct a claim ONLY when the sample plainly contradicts it. Do not shorten it, do not rewrite it from scratch:\n${priorProfile}`
      : '';
    const input = `PLAYER: ${w}\nENGAGEMENT SIGNALS: avg reply ~${signals.avgLen || '?'} chars, ${signals.longReplyPct ?? '?'}% are long invested replies, most active in: ${(signals.topChannels || []).join(', ') || 'various'}.${priorBlock}\n\nSAMPLE OF THEIR WRITING (chronological, spanning years; [OOC] = speaking as herself, weight these most):\n${sampleText}`;

    const { data: { session } = { session: null } } = await safeGetSession().catch(() => ({ data: { session: null } }));
    if (!session?.access_token) return;
    const res = await fetch(`${SUPABASE_URL}/functions/v1/ai-proxy`, {
      method: 'POST',
      // AI_ABORT_MS guard (audit P2-14; see aiTimeouts.js): a hung background call used to pin its promise across iOS suspend/resume forever.
      signal: AbortSignal.timeout(AI_ABORT_MS),
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
        'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({
        provider: 'claude-haiku',
        nsfw: true,
        systemPrompt: ANALYSIS_PROMPT,
        messages: [{ role: 'user', content: input }],
        // 6000 visible tokens: the structured class runs with reasoning OFF for
        // this feature (ai-proxy NO_REASONING_FEATURES), so the whole budget is
        // the profile itself — past runs wrote 930–5,249 tokens.
        maxTokens: 6000,
        feature: 'preference_analysis',
        characterId: null,
        characterName: null,
      }),
    });
    if (!res.ok) { markRefreshFailed(w, `HTTP ${res.status}`); return; }
    const data = await res.json();
    const profile = (data.text || '').trim();
    if (!profile) { markRefreshFailed(w, 'empty analysis'); return; }

    // CHECK THE WRITE. This upsert was refused by RLS on every call for as long
    // as the feature existed (the table had a SELECT policy and nothing else),
    // and because the result was never destructured, nothing logged it. The
    // cost of that silence: refreshPlayerPreferences is gated on updated_at
    // being stale, so a write that never lands leaves the row stale forever and
    // the analysis re-runs on EVERY app load — ~50k input tokens a time. 18
    // wasted runs and ~872k tokens had accumulated by the time the audit found
    // it. Policies added in 20260729020000; this is the tripwire so it can
    // never go quiet again.
    const { error: upsertErr } = await supabase.from('player_preferences').upsert({
      writer: w,
      profile,
      signals,
      based_on_count: sample.length,
      updated_at: new Date().toISOString(),
    }, { onConflict: 'writer' });
    if (upsertErr) {
      console.error('[playerPreferences] profile write FAILED — the analysis just ran and was thrown away:', upsertErr.code, upsertErr.message);
      // Don't let a broken write turn into an unbounded spend loop: the
      // staleness check can't clear, so without this every subsequent call
      // would pay for the same analysis again.
      markRefreshFailed(w, `write ${upsertErr.code}`);
    } else {
      try { localStorage.removeItem(backoffKey(w)); } catch { /* memory only */ }
    }
  } catch (err) {
    markRefreshFailed(w, err?.name === 'TimeoutError' ? 'timeout' : (err?.message || 'error'));
  }
}

/**
 * Refresh any player whose profile is missing or older than REFRESH_AFTER_DAYS.
 * Fire-and-forget; safe to call often (it self-throttles on updated_at).
 */
export async function maybeRefreshPreferences(displayNames) {
  const writers = toWriters(displayNames);
  if (!writers.length) return;
  try {
    const existing = await getPlayerPreferenceProfiles(displayNames);
    const cutoff = Date.now() - REFRESH_AFTER_DAYS * 86400000;
    for (const w of writers) {
      // A writer whose profile write already failed this session is skipped.
      // Staleness is measured on updated_at, so a rejected write can never
      // clear it — without this guard the same ~50k-token analysis is paid for
      // again on every single call, forever. That is exactly what happened
      // before the RLS policies existed.
      if (refreshBackedOff(w)) continue;
      const row = existing[w];
      const stale = !row || !row.updated_at || new Date(row.updated_at).getTime() < cutoff;
      if (stale) refreshPlayerPreferences(w); // fire-and-forget, one at a time
    }
  } catch { /* non-fatal */ }
}
